import { FROST_LADY_REST } from '../../models/frost-lady.ts';
import { clip, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * La Dama Helada (models/frost-lady.ts): she glides, never steps; everything she does is slow and long-handed.
 *
 * 'idle' (4 s) floats; 'walk' glides, leaning in, the arms trailing; 'attack' a slash of the right hand's ice
 * fingers (lands at 0.5 s). The rule: 'cast' (1.8 s) gathers a star of frost in her right hand held high (the
 * `spell` bone grows 0.2–0.85 s), thrusts it at the hero at 0.95 s and closes her fist as the ice takes him.
 * 'breath' (2.0 s): she draws in, then leans and breathes a cone of frost (the `breath` bone, 0.7–1.6 s).
 *
 * Conventions as in human.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow; +Z on
 * the right arm (+X) lifts it out to the side, −Z the left; +X on the trunk or the head bows it forward.
 * `breath` and `spell` are built tiny: only their clips scale them up (1 / FROST_LADY_REST).
 */

type B = 'root' | 'body' | 'chest' | 'head' | 'breath' | 'armL' | 'foreL' | 'handL' | 'armR' | 'foreR' | 'handR' | 'spell';
type P = KeyPose<B>;
const BIG = 1 / FROST_LADY_REST;

const idle = (): Clip<B> =>
  clip<B>('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    b.root.pos(0, 0.035 * Math.sin(p), 0);
    b.body.rot(0.01 * Math.sin(p), 0.03 * Math.sin(p + 0.5), 0.015 * Math.sin(p + 1));
    b.chest.rot(-0.03 * Math.sin(p * 2), 0.05 * Math.sin(p + 1), 0.02 * Math.sin(p));
    b.head.rot(0.05 + 0.04 * Math.sin(p + 2), -0.12 * Math.sin(p + 0.7), 0.05 * Math.sin(p));
    b.armL.rot(-0.15 + 0.05 * Math.sin(p + 1), 0, -0.14 - 0.04 * Math.sin(p));
    b.armR.rot(-0.15 + 0.05 * Math.sin(p + 2), 0, 0.14 + 0.04 * Math.sin(p));
    b.foreL.rot(-0.35 + 0.08 * Math.sin(p + 1.5));
    b.foreR.rot(-0.35 + 0.08 * Math.sin(p + 2.5));
    b.handL.rot(0.2 + 0.1 * Math.sin(p * 2), 0, 0);
    b.handR.rot(0.2 + 0.1 * Math.sin(p * 2 + 1), 0, 0);
  });

const walk = (dur = 2.4): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    b.root.pos(0, 0.04 + 0.03 * Math.sin(2 * th), 0);
    b.body.rot(0.1, 0.06 * Math.sin(th), 0.03 * Math.sin(th + 0.5));
    b.chest.rot(0.02, -0.06 * Math.sin(th), 0.02 * Math.sin(th));
    b.head.rot(-0.05, 0.04 * Math.sin(th + 1), 0);
    b.armL.rot(0.35 + 0.05 * Math.sin(th), 0, -0.2);
    b.armR.rot(0.35 - 0.05 * Math.sin(th), 0, 0.2);
    b.foreL.rot(-0.2);
    b.foreR.rot(-0.2);
    b.handL.rot(0.3 + 0.1 * Math.sin(th * 2), 0, 0);
    b.handR.rot(0.3 + 0.1 * Math.sin(th * 2 + 1), 0, 0);
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 1.3, false, (b, t) => {
    const up: P = { body: [-0.04, 0.15, 0], chest: [-0.12, 0.3, 0], head: [-0.05, -0.2, 0], armR: [-2.3, 0, 0.6], foreR: [-0.9, 0, 0], handR: [-0.3, 0, 0], armL: [-0.3, 0, -0.35], foreL: [-0.5, 0, 0] };
    const cut: P = { root: [0, 0, 0, 0, 0, 0.1], body: [0.12, -0.15, 0], chest: [0.25, -0.35, 0], head: [0.1, 0.15, 0], armR: [-0.6, 0, -0.35], foreR: [-0.1, 0, 0], handR: [0.4, 0, 0], armL: [0.2, 0, -0.4], foreL: [-0.4, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.38, up, inOut],
      [0.5, cut, inExpo],
      [0.75, { ...cut, chest: [0.22, -0.32, 0] }, outCubic],
      [1.3, {}, inOut],
    ], { head: 0.04, handR: 0.03 });
  });

/** Breath: she draws in, rearing, then bows her head and breathes the cone of frost, sweeping it a little. */
const breath = (): Clip<B> =>
  clip<B>('breath', 2.0, false, (b, t) => {
    const draw: P = { body: [-0.06, 0, 0], chest: [-0.22, 0, 0, 0, 0, 0, 1.04], head: [-0.4, 0, 0], armL: [-0.2, 0, -0.5], foreL: [-0.6, 0, 0], armR: [-0.2, 0, 0.5], foreR: [-0.6, 0, 0] };
    const blow: P = { root: [0, 0, 0, 0, 0, 0.06], body: [0.1, 0, 0], chest: [0.18, 0, 0], head: [0.32, 0, 0], armL: [-0.5, 0, -0.75], foreL: [-0.2, 0, 0], handL: [-0.3, 0, 0], armR: [-0.5, 0, 0.75], foreR: [-0.2, 0, 0], handR: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.5, draw, inOut],
      [0.7, blow, outCubic],
      [1.6, blow],
      [2.0, {}, inOut],
    ], { head: 0.04 });
    const on = envelope(t, 0.66, 0.78, 1.5, 1.65, outQuad);
    b.chest.rot(0, 0.2 * Math.sin((t - 0.7) * 3.4) * on, 0);
    b.breath.scale(1 + (BIG - 1) * on * (0.94 + 0.06 * Math.sin(t * 31)));
  }, undefined, 'Aliento helado');

/** The rule: she gathers the star of frost high in her right hand, thrusts it at a hero and closes her fist. */
const cast = (): Clip<B> =>
  clip<B>('cast', 1.8, false, (b, t) => {
    const gather: P = { root: [0, 0, 0, 0, 0.05, 0], body: [-0.06, 0.1, 0], chest: [-0.16, 0.2, 0], head: [-0.25, -0.1, 0], armR: [-2.8, 0, 0.25], foreR: [-0.35, 0, 0], handR: [-0.2, 0, 0], armL: [-0.9, 0, -0.1], foreL: [-1.6, 0, 0], handL: [0.2, 0, 0] };
    const thrust: P = { root: [0, 0, 0, 0, 0.02, 0.12], body: [0.1, -0.12, 0], chest: [0.22, -0.2, 0], head: [0.12, 0.05, 0], armR: [-1.55, 0, 0.05], foreR: [-0.05, 0, 0], handR: [-0.5, 0, 0], armL: [-0.1, 0, -0.5], foreL: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.75, gather, inOut],
      [0.85, { ...gather, armR: [-2.9, 0, 0.25] }, outQuad],
      [0.95, thrust, inExpo],
      [1.35, { ...thrust, handR: [0.9, 0, 0], foreR: [-0.25, 0, 0] }, outCubic],
      [1.8, {}, inOut],
    ], { head: 0.04, handR: 0.02 });
    // The star: grows spinning in the raised hand, flies with the thrust (gone by 1.05 s).
    const grow = span(t, 0.2, 0.85, outBack) * (1 - span(t, 0.95, 1.05, inQuad));
    b.spell.rot(0, 0, t * 5).pos(0, 0, 0.4 * span(t, 0.9, 1.05, inQuad)).scale(1 + (BIG - 1) * Math.max(0, grow) * (1 + 0.15 * span(t, 0.88, 0.95)));
  }, (t) => {
    const q = Math.max(0, settle(t - 0.95, 3, 8)) * (t > 0.95 ? 1 : 0);
    return [1 - 0.02 * q, 1 + 0.03 * q, 1 - 0.02 * q];
  }, 'Encierra en hielo');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.2, 8) * span(t, 0, 0.04, outQuad);
    b.root.pos(0, 0, -0.04 * q);
    b.body.rot(-0.05 * q);
    b.chest.rot(-0.12 * q, 0.06 * q, 0);
    b.head.rot(-0.2 * q, -0.1 * q, 0.08 * q);
    b.armL.rot(-0.2 * q, 0, -0.15 * q);
    b.armR.rot(-0.2 * q, 0, 0.15 * q);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.8, true, (b, t) => {
    const p = phase(t, 2.8) * TAU;
    b.root.pos(0, -0.04, 0).rot(0, 0, 0.03 * Math.sin(p));
    b.chest.rot(0.25, 0.08 * Math.sin(p), 0);
    b.head.rot(0.45 + 0.1 * Math.sin(p + 0.8), 0.25 * Math.cos(p + 0.8), 0.15 * Math.sin(p));
    b.armL.rot(0.1, 0, 0.05);
    b.armR.rot(0.1, 0, -0.05);
    b.foreL.rot(-0.1);
    b.foreR.rot(-0.1);
  });

/** Death (2.6 s): she arches, head thrown back, arms flung wide; then she sinks, folding, into her own gown. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const arch: P = { root: [0, 0, 0, 0, 0.08, 0], body: [-0.1, 0, 0], chest: [-0.3, 0, 0], head: [-0.6, 0, 0], armL: [-1.8, 0, -0.9], foreL: [-0.2, 0, 0], armR: [-1.8, 0, 0.9], foreR: [-0.2, 0, 0] };
    const sunk: P = { root: [0, 0, 0, 0, -0.95, 0], body: [0.2, 0, 0.08], chest: [0.65, 0.1, 0], head: [0.5, 0.3, 0.2], armL: [0.2, 0, 0.1], foreL: [-0.2, 0, 0], armR: [0.1, 0, -0.15], foreR: [-0.3, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.45, arch, outExpo],
      [0.9, { ...arch, chest: [-0.25, 0.1, 0] }, inOut],
      [2.0, sunk, inCubic],
      [2.6, { ...sunk, root: [0, 0, 0, 0, -1.0, 0] }, outQuad],
    ], { head: 0.06, foreL: 0.04, foreR: 0.04 });
    b.chest.rot(0.03 * Math.sin(t * 40) * envelope(t, 0.4, 0.5, 0.9, 1.1), 0, 0);
  });

/** Clips by library kind (see ../rigs.ts). */
export const frostLadyClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), breath(), cast()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { frostLady: frostLadyClips };
